import { useCallback, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { Plus, RefreshCw } from "lucide-react";

import { useAdapter, useTopology } from "@/store/site";
import { carryTo } from "@/lib/carryParams";
import { tr, useT } from "@/lib/i18n";
import { pushToast } from "@/hooks/useToasts";
import { markPending, resolvePending } from "@/store/pendingWrites";
import { useTargetableDevices } from "@/hooks/useAlarmRules";
import {
  generateRuleId,
  generateTemplateId,
  type AlarmRule,
  type AlarmRuleEnvelope,
  type DatapointInfo,
} from "@/data/alarmRules/wireFormat";
import type { AlarmTargetableDevice } from "@/data/adapter";
import type { RuleDraft, RuleTemplate } from "@/data/alarmRules/templates";

import { PageShell } from "./PageShell";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { RuleEditor } from "@/components/alarms/RuleEditor";
import { RuleList, type DeviceRuleSet } from "@/components/alarms/RuleList";
import type { AckState } from "@/components/alarms/AckStatusBadge";
import { TemplatesGallery } from "@/components/alarms/TemplatesGallery";

/**
 * Cloud-side alarm rule authoring page. Lists the rules stored on each
 * target device's `bess_alarm_rules` SHARED_SCOPE attribute, lets the
 * operator add/edit/disable rules, replicates one logical rule across
 * multiple devices, and surfaces the edge ACK via the per-device pip.
 */
export default function AlarmRulesPage() {
  const t = useT();
  const adapter = useAdapter();
  const topology = useTopology();
  const siteName = topology?.name ?? "";
  const { data: devices = [] } = useTargetableDevices();
  const qc = useQueryClient();

  const [editor, setEditor] = useState<
    | { kind: "create"; draft: RuleDraft; targets: AlarmTargetableDevice[] }
    | { kind: "edit"; rule: AlarmRule; deviceId: string }
    | null
  >(null);
  /** Per-device most-recent save timestamp (ms epoch). Drives the ACK pip. */
  const [pending, setPending] = useState<Record<string, number>>({});
  // F21: per-device pending-writes-store id for the in-flight rule save. The
  // AckStatusBadge's onAck (acknowledged / edge_error / timeout) resolves it —
  // we just reflect that same decision into the shared store; no new echo.
  const pendingWriteIdRef = useRef<Record<string, string>>({});

  const ruleQueries = useQueries({
    queries: devices.map((d) => ({
      queryKey: ["alarm-rules", d.id] as const,
      queryFn: () => adapter.listAlarmRules(d.id),
      staleTime: 10_000,
    })),
  });

  const perDevice: DeviceRuleSet[] = useMemo(
    () =>
      devices.map((d, i) => ({
        device: d,
        rules: (ruleQueries[i].data?.rules ?? []) as AlarmRule[],
        pendingSince: pending[d.id],
      })),
    [devices, ruleQueries, pending],
  );

  // Per-device `available_datapoints` catalog — feeds the "now 12.4 %"
  // live value next to each rule's config summary (ALM-08). Query key
  // matches DATAPOINTS_KEY in useAlarmRules so the cache is shared with
  // the editor's DatapointPicker; a gentle poll keeps the value honest
  // while the operator tunes thresholds against it.
  const datapointQueries = useQueries({
    queries: devices.map((d) => ({
      queryKey: ["alarm-rules-datapoints", d.id] as const,
      queryFn: () => adapter.getAvailableDatapoints(d.id),
      staleTime: 30_000,
      refetchInterval: 60_000,
    })),
  });

  const datapointsByDevice = useMemo(() => {
    const out: Record<string, DatapointInfo[]> = {};
    devices.forEach((d, i) => {
      out[d.id] = datapointQueries[i]?.data?.datapoints ?? [];
    });
    return out;
  }, [devices, datapointQueries]);

  /**
   * Map a rule's datapoint topic to Historical-analyzer keys — the same
   * topic→catalog-key approach the alarms console uses (alarmHistoricalKeys
   * in AlarmsPage). Honest by design: when the topic doesn't resolve to a
   * series we KNOW charts the same signal, return null and the list omits
   * the link instead of landing the operator on the wrong chart.
   */
  const historyHrefFor = useCallback(
    (device: AlarmTargetableDevice, rule: AlarmRule): string | null => {
      const dp = rule.datapoint;
      if (!dp) return null;
      // Pin the curated 24 h window; drop any carried forensic range so the
      // link is deterministic (env params still carry via carryTo).
      const link = (keys: string) =>
        carryTo("/historical", { keys, preset: "24h", start: null, end: null, gran: null });
      const powerish = (topic: string) => /(power|_w|_kw)$/i.test(topic.split("/").pop() ?? "");
      // Site-aggregation topics ARE curated Historical keys — chart directly.
      if (dp === "battery/aggregation/power" || dp === "battery/aggregation/SOC") return link(dp);
      if (device.kind === "bess") {
        const catalogKey = /^battery\/measurement\/\d+\/SOC$/i.test(dp)
          ? "soc"
          : dp.startsWith("battery/") && powerish(dp)
            ? "ac_active_power"
            : null;
        if (!catalogKey) return null;
        // Targetable-device id → topology unit id: exact id match (mock),
        // else the device-name fallback (live TB UUIDs, mirrors
        // adapter.bessIdForAlarm's reverse lookup).
        const unit =
          topology.bess.find((b) => b.id === device.id) ??
          topology.bess.find((b) => b.name === device.name || b.id === device.name);
        if (!unit) return null;
        const keys = adapter.historicalKeysFor(unit.id, [catalogKey]);
        return keys.length ? link(keys.join(",")) : null;
      }
      if (device.kind === "grid" && powerish(dp)) return link("grid_W");
      if (
        device.kind === "pv" &&
        powerish(dp) &&
        topology.smartmeters.some((m) => m.role === "pv")
      ) {
        return link("pv_power_W");
      }
      return null;
    },
    [adapter, topology],
  );

  const candidateChildrenByDevice = useMemo(() => {
    const out: Record<string, { id: string; name: string }[]> = {};
    for (const set of perDevice) {
      out[set.device.id] = set.rules
        .filter((r) => r.type !== "composite") // child must not itself be a composite to avoid trivial cycles
        .map((r) => ({ id: r.id, name: r.name }));
    }
    return out;
  }, [perDevice]);

  const refreshAll = useCallback(() => {
    for (const d of devices) {
      qc.invalidateQueries({ queryKey: ["alarm-rules", d.id] });
      qc.invalidateQueries({ queryKey: ["alarm-rules-status", d.id] });
    }
  }, [devices, qc]);

  const writeEnvelope = useCallback(
    async (deviceId: string, transform: (env: AlarmRuleEnvelope) => AlarmRuleEnvelope) => {
      const current = await adapter.listAlarmRules(deviceId);
      const next = transform(current);
      await adapter.saveAlarmRules(deviceId, next);
      setPending((p) => ({ ...p, [deviceId]: Date.now() }));
      // F21: reflect the write into the shared pending-writes store. Resolution
      // comes from onDeviceAck below — the SAME echo the AckStatusBadge polls.
      const deviceName = devices.find((d) => d.id === deviceId)?.name ?? deviceId;
      pendingWriteIdRef.current[deviceId] = markPending({
        id: `alarmRule-${deviceId}`,
        kind: "alarmRule",
        label: deviceName,
        href: carryTo("/alarms/rules"),
      });
      qc.invalidateQueries({ queryKey: ["alarm-rules", deviceId] });
      qc.invalidateQueries({ queryKey: ["alarm-rules-status", deviceId] });
    },
    [adapter, qc, devices],
  );

  // F21: mirror the per-device edge ACK into the shared pending-writes store
  // using AckStatusBadge's existing terminal states — acknowledged with no
  // edge errors → "applied"; edge_error → "rejected"; timeout → "unconfirmed"
  // (the edge is offline / hasn't echoed). No new echo logic.
  const onDeviceAck = useCallback((deviceId: string, state: AckState) => {
    const id = pendingWriteIdRef.current[deviceId];
    if (!id) return;
    if (state.kind === "acknowledged") {
      delete pendingWriteIdRef.current[deviceId];
      resolvePending(id, "applied");
    } else if (state.kind === "edge_error") {
      delete pendingWriteIdRef.current[deviceId];
      resolvePending(id, "rejected");
    } else if (state.kind === "timeout") {
      delete pendingWriteIdRef.current[deviceId];
      resolvePending(id, "unconfirmed");
    }
  }, []);

  const onSave = useCallback(
    async (params: { targets: string[]; rule: AlarmRule; templateId: string | undefined }) => {
      const { targets, rule, templateId } = params;
      const finalTemplateId = templateId ?? rule.template_id ?? generateTemplateId();
      const results: { deviceId: string; ok: boolean; error?: string }[] = [];
      for (const deviceId of targets) {
        try {
          await writeEnvelope(deviceId, (env) => {
            // Replace existing rule with same `id` if present, else by `template_id`,
            // else append. This keeps "edit all" idempotent across replicas.
            const existing = env.rules.findIndex(
              (r) =>
                r.id === rule.id ||
                (finalTemplateId !== undefined && r.template_id === finalTemplateId),
            );
            const stamped: AlarmRule = {
              ...rule,
              id: existing >= 0 ? env.rules[existing].id : generateRuleId(),
              template_id: finalTemplateId,
              updatedAt: Date.now(),
              createdAt: existing >= 0 ? env.rules[existing].createdAt ?? Date.now() : Date.now(),
            };
            const nextRules =
              existing >= 0
                ? env.rules.map((r, i) => (i === existing ? stamped : r))
                : [...env.rules, stamped];
            return { ...env, version: 4, rules: nextRules };
          });
          results.push({ deviceId, ok: true });
        } catch (err) {
          results.push({
            deviceId,
            ok: false,
            error: err instanceof Error ? err.message : String(err),
          });
        }
      }
      const ok = results.filter((r) => r.ok).length;
      const failed = results.filter((r) => !r.ok);
      if (failed.length === 0) {
        pushToast({
          level: "ok",
          title: ok === 1 ? tr("manage.rules.savedOk.one") : tr("manage.rules.savedOk.many", { count: ok }),
        });
      } else if (ok > 0) {
        pushToast({
          level: "warn",
          title: tr("manage.rules.savedPartial", { ok, failed: failed.length, error: failed[0].error ?? "?" }),
        });
      } else {
        pushToast({
          level: "critical",
          title: tr("manage.saveFailed", { error: failed[0]?.error ?? "unknown" }),
        });
      }
    },
    [writeEnvelope],
  );

  const onEditOne = (deviceId: string, ruleId: string) => {
    const set = perDevice.find((p) => p.device.id === deviceId);
    const rule = set?.rules.find((r) => r.id === ruleId);
    if (!rule) return;
    // Detach: drop template_id so subsequent "edit all" on its siblings
    // doesn't touch this replica. Confirm via toast.
    const detached: AlarmRule = { ...rule, template_id: undefined };
    setEditor({ kind: "edit", rule: detached, deviceId });
  };

  const onEditAll = (templateId: string) => {
    // Pick any one replica as the reference; saving will fan out.
    let reference: AlarmRule | null = null;
    let referenceDevice: string | null = null;
    for (const set of perDevice) {
      const r = set.rules.find((rr) => rr.template_id === templateId);
      if (r) {
        reference = r;
        referenceDevice = set.device.id;
        break;
      }
    }
    if (reference && referenceDevice) {
      setEditor({ kind: "edit", rule: reference, deviceId: referenceDevice });
    }
  };

  const onToggleEnabled = (deviceId: string, ruleId: string, enabled: boolean) => {
    void writeEnvelope(deviceId, (env) => ({
      ...env,
      version: 4,
      rules: env.rules.map((r) => (r.id === ruleId ? { ...r, enabled, updatedAt: Date.now() } : r)),
    }));
  };

  // G25: a destructive write must not complete silently. Await the write and
  // surface a success/failure toast (announced via the Toaster's live region).
  const onDelete = async (deviceId: string, ruleId: string) => {
    try {
      await writeEnvelope(deviceId, (env) => ({
        ...env,
        version: 4,
        rules: env.rules.filter((r) => r.id !== ruleId),
      }));
      pushToast({ level: "ok", title: tr("manage.rules.deletedOk") });
    } catch (err) {
      pushToast({
        level: "critical",
        title: tr("manage.rules.deleteFailed"),
        detail: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const onDeleteGroup = async (templateId: string) => {
    let affected = 0;
    try {
      for (const set of perDevice) {
        if (!set.rules.some((r) => r.template_id === templateId)) continue;
        await writeEnvelope(set.device.id, (env) => ({
          ...env,
          version: 4,
          rules: env.rules.filter((r) => r.template_id !== templateId),
        }));
        affected++;
      }
      if (affected > 0) pushToast({ level: "ok", title: tr("manage.rules.deletedOk") });
    } catch (err) {
      pushToast({
        level: "critical",
        title: tr("manage.rules.deleteFailed"),
        detail: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const onPickTemplate = (template: RuleTemplate, targets: AlarmTargetableDevice[]) => {
    if (targets.length === 0) {
      pushToast({
        level: "warn",
        title: tr("manage.rules.pickDevice"),
      });
      return;
    }
    const draft = template.build(targets[0]);
    setEditor({ kind: "create", draft, targets });
  };

  const onNew = () => {
    if (devices.length === 0) {
      pushToast({
        level: "warn",
        title: tr("manage.rules.noDevices"),
      });
      return;
    }
    const target = devices[0];
    const draft: RuleDraft = {
      __draft: true,
      name: "",
      type: "threshold",
      enabled: true,
      severity: "WARNING",
      datapoint: "",
      config: {
        operator: ">",
        threshold: 0,
        hysteresis: 0,
        duration_s: 0,
      },
      alarm_message: "",
      deliveryMethod: "notification",
      // v4 — set explicit defaults so the editor's Behaviour panel shows
      // the right radio selected from first render (auto_clear ON,
      // propagate TENANT). The zod schema defaults are picked up at
      // parse time; the form's `defaultValues` need them set explicitly
      // for the controlled radio to show as checked.
      auto_clear: true,
      propagate: "TENANT",
      notification: null,
    };
    setEditor({ kind: "create", draft, targets: [target] });
  };

  return (
    <PageShell
      title={t("manage.rules.title")}
      caption={siteName ? t("manage.rules.caption", { site: siteName }) : undefined}
      actions={
        // Mirror of the Alarms hub's Live | Rules switch (F01): here Rules is
        // the active segment and Live links back to the alarms console — so
        // rule authoring reads as a sub-view of the alarm hub, not an orphan.
        <div className="flex items-center gap-1 rounded-md border border-edge bg-surface p-0.5 text-[11px]">
          <Link to="/alarms" className="rounded px-2 py-1 text-text-muted hover:text-text">
            {t("manage.alarms.tabLive")}
          </Link>
          <span aria-current="page" className="rounded px-2 py-1 bg-accent/15 text-accent">
            {t("manage.alarms.tabRules")}
          </span>
        </div>
      }
    >
      <Card>
        <CardHeader>
          <CardTitle>{t("manage.rules.rules")}</CardTitle>
          <div className="flex items-center gap-2">
            <TemplatesGallery
              defaultTargets={devices}
              onPick={onPickTemplate}
            />
            <button
              type="button"
              onClick={refreshAll}
              className="text-[11px] uppercase tracking-wide px-2 py-1.5 rounded border border-edge hover:bg-surface-2/60 flex items-center gap-1"
              title={t("manage.rules.refreshTitle")}
            >
              <RefreshCw className="size-3.5" />
            </button>
            <button
              type="button"
              onClick={onNew}
              className="text-[11px] uppercase tracking-wide px-3 py-1.5 rounded border border-accent/60 bg-accent/15 text-accent hover:bg-accent/25 flex items-center gap-1"
            >
              <Plus className="size-3.5" /> {t("manage.rules.newRule")}
            </button>
          </div>
        </CardHeader>
        <div className="p-3">
          <RuleList
            perDevice={perDevice}
            datapointsByDevice={datapointsByDevice}
            historyHrefFor={historyHrefFor}
            onEditOne={onEditOne}
            onEditAll={onEditAll}
            onToggleEnabled={onToggleEnabled}
            onDelete={onDelete}
            onDeleteGroup={onDeleteGroup}
            onDeviceAck={onDeviceAck}
          />
        </div>
      </Card>

      {editor && (
        <RuleEditor
          mode={editor}
          candidateChildrenByDevice={candidateChildrenByDevice}
          onClose={() => setEditor(null)}
          onSave={onSave}
        />
      )}
    </PageShell>
  );
}
